/**
 * props — node prop resolution (the boring half of §4.2, isolated so the
 * interpreter stays readable).
 *
 * The `dataProps`/`nodeProps` lists come from `registry.propSpecFor(type)` —
 * the REGISTRY-declared arrays when the entry carries them, the hardcoded KIT
 * defaults otherwise. That order is what lets a hot-shipped component (an L3
 * Domain composition that exists only as data, or one this build cannot render
 * at all) still declare which of its props are paths, so its bindings resolve
 * — or degrade — the same way a baked component's do.
 *
 * Three rules, applied in this order:
 *   1. A prop the kit declares as `nodeProps` is passed through VERBATIM. A
 *      grid's `card` is a component TEMPLATE, not a value: resolving it here
 *      would evaluate `item.*` against a context that has no row yet and bake
 *      the template into one empty card.
 *   2. EVERY other string prop gets `{…}` interpolation ("Log to {totals.day}"),
 *      and every `{bind}` / `{$}` / `{agg}` object — at ANY depth — resolves.
 *   3. Only the props a kit entry declares in `dataProps` are read as context
 *      PATHS ("totals.calories" → 1840). An unresolvable path falls back to
 *      the literal string, so a typo shows up as visible text rather than a
 *      blank — the same fail-loud instinct as the unsupported placeholder.
 */
import { getPath, hasInterp, interpolate, isBindSpec, pathRoot, resolveValue, SCOPE_ROOTS } from './paths';

/**
 * A declared data prop resolves as a PATH when it is rooted at a real context
 * key ("totals.calories", "page_args.date") or at a SCOPE root ("item.name") —
 * including when that path is currently absent, which yields undefined rather
 * than the path string. Only a string that is NOT such a path keeps its
 * literal value, so a typo'd root ("total.calories") still shows up as visible
 * text instead of vanishing.
 *
 * `item` is in that set even though it is absent from the page context: it
 * exists only inside a grid's card template, and a card must bind it the same
 * way every other scope binds.
 */
function isContextPath(value, ctx) {
  if (!value.includes('.')) return Object.prototype.hasOwnProperty.call(ctx, value);
  const head = pathRoot(value);
  return SCOPE_ROOTS.has(head) || Object.prototype.hasOwnProperty.call(ctx, head);
}

function deepResolve(value, ctx) {
  if (isBindSpec(value)) return resolveValue(value, ctx);
  if (typeof value === 'string') return hasInterp(value) ? interpolate(value, ctx) : value;
  if (Array.isArray(value)) return value.map((v) => deepResolve(v, ctx));
  if (value && typeof value === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(value)) out[k] = deepResolve(v, ctx);
    return out;
  }
  return value;
}

export function resolveProps(rawProps, kit, ctx) {
  const src = rawProps && typeof rawProps === 'object' ? rawProps : {};
  const wanted = new Set(kit?.dataProps || []);
  const verbatim = new Set(kit?.nodeProps || []);
  const out = {};
  for (const [key, value] of Object.entries(src)) {
    if (verbatim.has(key)) { out[key] = value; continue; }
    if (isBindSpec(value)) { out[key] = resolveValue(value, ctx); continue; }
    if (wanted.has(key) && typeof value === 'string' && !hasInterp(value)) {
      if (isContextPath(value, ctx)) { out[key] = getPath(ctx, value); continue; }
      out[key] = value;
      continue;
    }
    out[key] = deepResolve(value, ctx);
  }
  return out;
}

/** `"debounce(250)"` → 250 ms; anything else → 0. */
export function parseRate(rate) {
  if (typeof rate !== 'string') return 0;
  const m = /^debounce\((\d+)\)$/.exec(rate.trim());
  return m ? parseInt(m[1], 10) : 0;
}
